import { useEffect, useState } from 'react';
import RoadmapItem from "../misc/roadMapItem.tsx";
import Typography from "../misc/typography.tsx";
import TopHeader from "../misc/topHeader.tsx";
import { roadmapData } from "../../data/roadmap.ts";

const Roadmap = () => {
  const [activePhase, setActivePhase] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredPhase, setHoveredPhase] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    const roadmapSection = document.getElementById('roadmap');
    if (roadmapSection) {
      observer.observe(roadmapSection);
    }

    return () => {
      if (roadmapSection) {
        observer.unobserve(roadmapSection);
      }
    };
  }, []);

  return (
    <div
      className={`relative min-h-screen p-8 bg-[#0A0A0F] 
        transform transition-all duration-1000 overflow-hidden
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      id="roadmap"
    >
      {/* Animated Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,rgba(99,102,241,0.15),rgba(0,0,0,0)_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,rgba(99,102,241,0.1),rgba(0,0,0,0)_50%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.05)_1px,transparent_1px)] bg-[size:40px_100%]" 
        style={{ maskImage: 'linear-gradient(to bottom, transparent, black, transparent)' }} />

      <div className="container relative z-10 mx-auto px-4">
        <div className="transform transition-all duration-500 hover:scale-105 mb-16">
          <TopHeader title="The Adventure Ahead" />
        </div>

        <Typography className="text-center mb-16 text-5xl font-bold bg-clip-text text-transparent 
          bg-gradient-to-r from-primary-400 to-primary-400 animate-gradient-x">
          ROADMAP
        </Typography>

        {/* Progress Bar */}
        <div className="fixed top-0 left-0 h-1.5 bg-gradient-to-r from-primary-500 to-primary-500" 
          style={{ 
            width: `${(activePhase / roadmapData.length) * 100}%`, 
            transition: 'width 0.5s ease-in-out',
            boxShadow: '0 0 30px rgba(99,102,241,0.3)'
          }} />

        {/* Phase Navigation */}
        <div className="flex justify-center gap-6 mb-20">
          {roadmapData.map((item, index) => (
            <button
              key={index}
              className="group relative"
              onMouseEnter={() => setHoveredPhase(index + 1)}
              onMouseLeave={() => setHoveredPhase(null)}
              onClick={() => setActivePhase(index + 1)}
            >
              <div className={`w-5 h-5 rounded-full transition-all duration-300 
                ${activePhase >= index + 1 
                  ? 'bg-gradient-to-r from-primary-500 to-primary-500' 
                  : 'bg-gray-700'}
                hover:scale-125 cursor-pointer relative z-10`}
              />
              {hoveredPhase === index + 1 && (
                <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 
                  bg-gray-800 px-3 py-1.5 rounded-lg text-sm text-primary-400 whitespace-nowrap
                  border border-primary-500/20 backdrop-blur-sm">
                  {item.phase}
                </div>
              )}
              <div className="absolute inset-0 bg-primary-500/20 rounded-full blur-xl 
                transition-opacity duration-300 opacity-0 group-hover:opacity-100" />
            </button>
          ))}
        </div>

        <div className="relative max-w-5xl mx-auto z-10">
          {roadmapData.map((item, index, array) => {
            const isLastItem = index === array.length - 1;
            const isActive = activePhase >= index + 1;
            return (
              <div
                key={index}
                className={`transform transition-all duration-500 
                  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                  ${index > 0 ? 'transition-delay-' + (index * 200) : ''}
                  ${!isActive ? 'opacity-50' : ''}`}
              >
                <RoadmapItem
                  phase={item.phase}
                  date={item.date}
                  items={item.items}
                  avatar={item.avatar}
                  isLeft={index % 2 === 0}
                  isLastItem={isLastItem}
                  isActive={isActive}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Roadmap;
